import { just, nothing, type Maybe } from "../lib/maybe";

export type EditorWordRange = {
  text: string;
  from: number;
  to: number;
};

const WORD_LEFT = /[\p{L}\p{N}''-]+$/u;
const WORD_RIGHT = /^[\p{L}\p{N}''-]+/u;

/**
 * Editor-agnostic core: `raw` is the selected text starting at `from`.
 * Trims surrounding whitespace; Nothing when only whitespace was selected.
 */
export function trimmedSelection(
  raw: string,
  from: number,
): Maybe<EditorWordRange> {
  const lead = raw.match(/^\s*/)?.[0].length ?? 0;
  const trail = raw.match(/\s*$/)?.[0].length ?? 0;
  const start = from + lead;
  const end = from + raw.length - trail;
  if (start >= end) return nothing;
  return just({
    text: raw.slice(lead, raw.length - trail),
    from: start,
    to: end,
  });
}

/**
 * Editor-agnostic core: the word around `offset` in `text` (a line or
 * textblock whose characters map 1:1 to positions starting at `base`).
 */
export function wordAround(
  text: string,
  offset: number,
  base: number,
): Maybe<EditorWordRange> {
  const left = text.slice(0, offset).match(WORD_LEFT);
  const right = text.slice(offset).match(WORD_RIGHT);
  const start = left !== null ? offset - left[0].length : offset;
  const end = right !== null ? offset + right[0].length : offset;
  if (start === end) return nothing;
  const word = text.slice(start, end).trim();
  return word.length > 0
    ? just({ text: word, from: base + start, to: base + end })
    : nothing;
}
